/**
 * Which existing bookings a change to the opening hours would hide.
 *
 * Closing a day does not cancel anything. createSpecialHours and
 * updateSpecialHours never touch the summaries — opening hours are read once,
 * when a summary is built, and existing summaries are never rewritten. So the
 * bookings stay exactly where they are and simply stop being shown by
 * everything that filters on opening hours at read time.
 *
 * That is why this is worth surfacing: nothing else tells the admin, and the
 * member still believes they have a court.
 *
 * Reads the day's slots and applies the rule here rather than asking the
 * backend a question shaped like a time range — see slotSurvives for why no
 * range can express it.
 */
import { getSlotsAdmin } from "../../api/summaries/summaries";
import { getRehabSlotsByAdmin, cancelRehabBooking } from "../../api/rehab/rehab";
import { cancelBooking } from "../../api/bookings/bookings";
import { getUserByMemberId } from "../../api/users/users";
import { capitalizeWords } from "../../shared/utility";
import {
  ICancelBookingInputType,
  ICourt,
  ICourtType,
  IRehabBooking,
  IRehabSummaryItem,
} from "../../types/api";

// Declared in each opening-hours popup rather than shared, so it is restated
// here instead of importing from one of them.
export type FacilityCourtType = "INDOOR" | "OUTDOOR" | "GYM" | "REHAB";

export interface IOpeningHoursConflict {
  court_id: string;
  date: string;
  start_time: string;
  end_time: string;
  type: string;
  conflict_name: string;
  /**
   * What it would take to cancel this. Absent for activities — those are not
   * bookings and are removed from the slot by a different resolver, so there
   * is nothing here for the UI to offer a button against.
   */
  cancel?:
    | { kind: "court"; input: ICancelBookingInputType }
    | { kind: "rehab"; input: IRehabBooking };
}

/** Stable identity for a row, for tracking which ones have been cancelled. */
export const conflictKey = (conflict: IOpeningHoursConflict) =>
  `${conflict.court_id}#${conflict.start_time}`;

export async function cancelConflict(
  conflict: IOpeningHoursConflict,
  sendEmail: boolean
): Promise<boolean> {
  if (!conflict.cancel) return false;
  if (conflict.cancel.kind === "rehab") {
    return !!(await cancelRehabBooking(conflict.cancel.input, sendEmail, true));
  }
  return !!(await cancelBooking(
    conflict.cancel.input,
    "admin",
    false,
    false,
    sendEmail
  ));
}

const DAY_START = "00:00:00";
const DAY_END = "23:59:59";

/**
 * Whether a slot survives the new opening hours.
 *
 * Everything that reads slots filters by containment — get_slots in the
 * statistics lambda uses `start >= opening and end <= closing`, and the coach
 * grid builds its ladder the same way. So a slot only stays visible if it fits
 * wholly inside the new hours, and one that straddles either edge disappears
 * just as completely as one lying entirely outside.
 *
 * That is why this is a predicate over slots rather than a set of time windows
 * to query: a slot running 10:30-11:30 against hours of 10:00-11:00 is outside
 * every window you could ask about, yet it is precisely the case that vanishes.
 */
export const slotSurvives = (
  slotStart: string,
  slotEnd: string,
  closed: boolean,
  openingHours?: string,
  closingHours?: string
): boolean => {
  if (closed) return false;
  return (
    slotStart >= (openingHours ?? DAY_START) &&
    slotEnd <= (closingHours ?? DAY_END)
  );
};

/** Types backed by per-court summaries. Rehab is handled separately below. */
const COURT_BACKED_TYPES = ["INDOOR", "OUTDOOR"];

/**
 * Rehab bookings live in a single RehabSummary per date rather than one
 * summary per court, so getSlotsAdmin does not return them. One extra query
 * covers the whole facility.
 */
async function findRehabConflicts(
  date: string,
  closed: boolean,
  openingHours?: string,
  closingHours?: string
): Promise<IOpeningHoursConflict[]> {
  const summary = await getRehabSlotsByAdmin(date);
  // The wrapper returns false on failure, so a plain truthiness check is not
  // enough to know a summary came back.
  if (!summary || typeof summary !== "object" || "message" in summary) return [];

  // booking is null on a free slot rather than absent, and the day is mostly
  // free slots.
  const slots: IRehabSummaryItem[] = summary.slots ?? [];
  const affected = slots.filter(
    (slot) =>
      slot?.booking?.member_id &&
      !slotSurvives(
        slot.start_time,
        slot.end_time,
        closed,
        openingHours,
        closingHours
      )
  );

  // A rehab booking carries only a member_id, unlike a court booking which
  // embeds member_name — so the names have to be looked up. One request per
  // distinct member, not per booking, since the same person often has several
  // slots in a day.
  const names = new Map<string, string>();
  await Promise.all(
    Array.from(new Set(affected.map((slot) => slot.booking.member_id))).map(
      async (memberId) => {
        try {
          const user = await getUserByMemberId(memberId);
          const name = [user?.first_name, user?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
          if (name) names.set(memberId, name);
        } catch {
          // A missing name is not worth failing the whole check for — the row
          // falls back to the id below.
        }
      }
    )
  );

  return affected.map((slot) => {
    const memberId = slot.booking.member_id;
    const name = names.get(memberId);
    return {
      court_id: REHAB_COURT_ID,
      date,
      start_time: slot.start_time,
      end_time: slot.end_time,
      type: "booking",
      conflict_name: name
        ? `${capitalizeWords(name)} (${memberId})`
        : `Medlem ${memberId}`,
      cancel: { kind: "rehab" as const, input: slot.booking },
    };
  });
}

const REHAB_COURT_ID = "rehab-1891";

export async function findOpeningHoursConflicts(params: {
  date: string;
  courtTypes: FacilityCourtType[];
  closed: boolean;
  openingHours?: string;
  closingHours?: string;
  courts?: ICourt[];
}): Promise<IOpeningHoursConflict[]> {
  const { date, courtTypes, closed, openingHours, closingHours } = params;

  // Nothing is being taken away, so nothing can be hidden.
  if (!closed && !openingHours && !closingHours) return [];

  const rehab = courtTypes.includes("REHAB")
    ? await findRehabConflicts(date, closed, openingHours, closingHours)
    : [];

  const types = courtTypes.filter((type) => COURT_BACKED_TYPES.includes(type));
  if (!types.length) return rehab;

  // One query per court type, returning the whole day. The predicate is then
  // applied here rather than being expressed as a time range for the backend
  // to match — see slotSurvives for why a range cannot express it.
  const summaries = (
    await Promise.all(types.map((type) => getSlotsAdmin(date, type as ICourtType)))
  ).flat();

  const courtConflicts: IOpeningHoursConflict[] = [];
  for (const summary of summaries) {
    for (const slot of summary.slots ?? []) {
      const held = slot.booking?.length > 0 || !!slot.activity;
      if (!held) continue;
      if (slotSurvives(slot.start_time, slot.end_time, closed, openingHours, closingHours))
        continue;

      courtConflicts.push({
        court_id: summary.court_id,
        date,
        start_time: slot.start_time,
        end_time: slot.end_time,
        type: slot.activity ? "activity" : "booking",
        conflict_name: slot.activity
          ? slot.activity.name.split("%-%")[0]
          : `${slot.booking[0].member_name} (${slot.booking[0].member_id})`,
        cancel: slot.activity
          ? undefined
          : {
              kind: "court" as const,
              input: {
                booking_id: slot.booking[0].booking_id,
                datetime_start: slot.booking[0].datetime_start,
                court_id: summary.court_id,
                slot_index: slot.booking[0].slot_index,
                coach_id: slot.booking[0].coach_id ?? [],
              } as ICancelBookingInputType,
            },
      });
    }
  }

  return [...rehab, ...courtConflicts];
}

const CATEGORY_LABELS: Record<string, string> = {
  INDOOR: "Inne",
  OUTDOOR: "Ute",
  REHAB: "Rehab",
};

/** One row, split into the columns the list renders. */
export const conflictColumns = (
  conflict: IOpeningHoursConflict,
  courts: ICourt[]
) => {
  const court = courts.find((c) => c.court_id === conflict.court_id);
  const isRehab = conflict.court_id === REHAB_COURT_ID;
  return {
    category: isRehab
      ? CATEGORY_LABELS.REHAB
      : CATEGORY_LABELS[court?.court_type ?? ""] ?? "",
    // Rehab has no courts, and repeating "Rehab-badet" next to the Rehab
    // category said nothing and wrapped onto two lines.
    court: isRehab ? "–" : court?.name ?? conflict.court_id,
    time: `${conflict.start_time.slice(0, 5)}–${conflict.end_time.slice(0, 5)}`,
    who: conflict.conflict_name,
  };
};
